// CHA-141 — AI usage: pure aggregation over a tenant's agent_audit records (CHA-140).
//
// Platform and tenant-agnostic: callers pass the records they read for one tenant; nothing here
// names a tenant or touches Firestore. Months and days are UTC, so a bucket never depends on the
// viewer's clock. Costs are the per-call estimates CHA-140 stored (`costEstimateUsd`, priced by the
// served model); a record without one (an unknown model, or a call that never reached the provider)
// is counted as unpriced, never as $0.
import { escCsv } from '../../shared/utils/csvSafe';

const millisOf = (v) => {
  if (v && typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : null; }
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
};
const num = (v) => (Number.isFinite(v) && v > 0 ? v : 0);
const pad2 = (n) => String(n).padStart(2, '0');

// 'YYYY-MM' -> { start, end } as UTC Dates, end exclusive. Null for anything that is not a month.
export function monthRange(month) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!m) return null;
  const y = Number(m[1]); const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  return { start: new Date(Date.UTC(y, mo - 1, 1)), end: new Date(Date.UTC(y, mo, 1)) };
}

export function currentMonth(now = new Date()) {
  return `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}`;
}

const emptyBucket = () => ({ calls: 0, errors: 0, inputTokens: 0, outputTokens: 0, cacheTokens: 0, costUsd: 0, unpriced: 0 });

function add(bucket, r) {
  const u = r.usage || {};
  bucket.calls += 1;
  if (r.outcome !== 'ok') bucket.errors += 1;
  bucket.inputTokens += num(u.inputTokens);
  bucket.outputTokens += num(u.outputTokens);
  bucket.cacheTokens += num(u.cacheCreationInputTokens) + num(u.cacheReadInputTokens);
  if (Number.isFinite(r.costEstimateUsd)) bucket.costUsd += r.costEstimateUsd;
  else bucket.unpriced += 1;
}

// Sum float costs in micro-dollars' worth of precision so the ledger's rounded per-call values
// don't drift into float noise on the way to a total.
const roundUsd = (v) => Math.round(v * 1e8) / 1e8;
const finish = (b) => ({ ...b, costUsd: roundUsd(b.costUsd) });

function groupBy(records, keyOf) {
  const out = new Map();
  for (const r of records) {
    const k = keyOf(r);
    if (!out.has(k)) out.set(k, emptyBucket());
    add(out.get(k), r);
  }
  return [...out.entries()].map(([key, b]) => ({ key, ...finish(b) })).sort((a, b) => b.costUsd - a.costUsd || b.calls - a.calls || a.key.localeCompare(b.key));
}

// The served model is what was billed; fall back to the requested one when the provider never answered.
const modelOf = (r) => r.modelServed || r.modelRequested || 'unknown';
const featureOf = (r) => (r.caller && r.caller.feature) || 'unknown';

export function summarizeUsage(records) {
  const totals = emptyBucket();
  for (const r of records) add(totals, r);
  return { totals: finish(totals), byFeature: groupBy(records, featureOf), byModel: groupBy(records, modelOf) };
}

// One row per UTC day of the month (zero-filled), so the chart shows quiet days as quiet.
export function dailySeries(records, month) {
  const range = monthRange(month);
  if (!range) return [];
  const days = [];
  for (let t = range.start.getTime(); t < range.end.getTime(); t += 86400000) {
    const d = new Date(t);
    days.push({ day: `${month}-${pad2(d.getUTCDate())}`, ...emptyBucket() });
  }
  for (const r of records) {
    const t = millisOf(r.startedAt);
    if (t === null || t < range.start.getTime() || t >= range.end.getTime()) continue;
    add(days[new Date(t).getUTCDate() - 1], r);
  }
  return days.map(finish);
}

export const CSV_HEADERS = ['startedAt (UTC)', 'feature', 'uid', 'modelRequested', 'modelServed', 'outcome',
  'inputTokens', 'outputTokens', 'cacheWriteTokens', 'cacheReadTokens', 'costEstimateUsd', 'pricingVersion',
  'durationMs', 'auditId'];

// Metadata only — the ledger holds no prompt, document or output text, and neither does this.
export function usageCsv(records) {
  const rows = [...records]
    .sort((a, b) => (millisOf(a.startedAt) ?? 0) - (millisOf(b.startedAt) ?? 0))
    .map((r) => {
      const u = r.usage || {};
      const t = millisOf(r.startedAt);
      return [t === null ? '' : new Date(t).toISOString(), featureOf(r), (r.caller && r.caller.uid) || '',
        r.modelRequested || '', r.modelServed || '', r.outcome || '',
        u.inputTokens ?? '', u.outputTokens ?? '', u.cacheCreationInputTokens ?? '', u.cacheReadInputTokens ?? '',
        Number.isFinite(r.costEstimateUsd) ? r.costEstimateUsd : '', r.pricingVersion || '',
        r.durationMs ?? '', r.auditId || ''];
    });
  return [CSV_HEADERS, ...rows].map((row) => row.map(escCsv).join(',')).join('\n');
}
